import { IContractEquipment } from 'features/inventory/contracts/types';
import {
  contractEquipmentService,
  equipmentHistoryLineService,
  equipmentService,
  examService,
  laboratoryService,
  tenantSettingsService,
  workstationService
} from 'shared/services';
import { Option, StatusOption } from 'shared/types/general';
import { EquipmentFormValues, EquipmentLinkMode } from '../types';
import { operationalUnitService } from 'features/settings/topology/api/operational-unit.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { sectorService } from 'features/settings/topology/api/sector.service';

const mapBaseInfoOptions = (items: { id: number; description: string }[]): Option[] =>
  items.map((item) => ({
    id: item.id,
    code: item.description
  }));

const normalizeMarketValue = (values: EquipmentFormValues): EquipmentFormValues => ({
  ...values,
  market_value: values.market_value ? values.market_value.replace(',', '.') : values.market_value
});

export const equipmentApi = {
  listService: equipmentService,
  getById: (id: number) => equipmentService.get(id),
  create: (values: EquipmentFormValues) => equipmentService.add(normalizeMarketValue(values)),
  update: (id: number, values: EquipmentFormValues) =>
    equipmentService.update(id, normalizeMarketValue(values)),
  getFormOptions: async () => {
    const [optionsResponse, warehouses] = await Promise.all([
      equipmentService.options(),
      warehouseService.getAllBaseInformation()
    ]);

    const statusOptions: StatusOption[] = optionsResponse?.actions?.POST?.status?.choices ?? [];
    const ivdrOptions: StatusOption[] = optionsResponse?.actions?.POST?.ivdr_class?.choices ?? [];

    return { statusOptions, ivdrOptions, warehouses };
  },
  getIvdrOptions: async (): Promise<StatusOption[]> => {
    const res = await equipmentService.options();
    return res?.actions?.POST?.ivdr_class?.choices ?? [];
  },
  getContractEquipments: async (equipmentId: number): Promise<IContractEquipment[]> => {
    const res = await contractEquipmentService.getAllPaginated(100, 0, {
      equipment_id: equipmentId,
      active: true
    });
    return res.results;
  },
  removeCostLink: (equipmentId: number, model: string, objectId: number) =>
    equipmentService.removeCostLink(equipmentId, { model, object_id: objectId }),
  addCostLink: (equipmentId: number, model: string, objectId: number) =>
    equipmentService.addCostLink(equipmentId, { model, object_id: objectId }),
  getLinkOptions: async (mode: EquipmentLinkMode): Promise<Option[]> => {
    if (!mode) return [];
    if (mode === 'laboratories') {
      return mapBaseInfoOptions(await laboratoryService.getAllBaseInformation());
    }
    if (mode === 'operational_units') {
      return mapBaseInfoOptions(await operationalUnitService.getAllBaseInformation());
    }
    if (mode === 'sectors') return mapBaseInfoOptions(await sectorService.getAllBaseInformation());
    if (mode === 'workstations') {
      return mapBaseInfoOptions(await workstationService.getAllBaseInformation());
    }
    if (mode === 'exams') return mapBaseInfoOptions(await examService.getAllBaseInformation());
    return [];
  },
  updateHistoryLine: (id: number, datetime: string) =>
    equipmentHistoryLineService.update(id, { datetime }),
  deleteHistoryLine: (id: number) => equipmentHistoryLineService.delete(id),
  getTenantSettings: () => tenantSettingsService.get(1)
};
